export const whatsappErrorMessage = (error) => {
  const detail = error?.response?.data?.detail || error?.response?.data?.error;
  if (typeof detail === 'string') return detail;
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return 'WhatsApp request timed out. Check the conversation before retrying this customer.';
  }
  return error?.message || 'Failed to send WhatsApp message';
};

const waitBetweenMessages = async (milliseconds, shouldStop) => {
  const until = Date.now() + milliseconds;
  while (!shouldStop() && Date.now() < until) {
    await new Promise(resolve => setTimeout(resolve, Math.min(250, until - Date.now())));
  }
};

// Keep the recipient list fixed while allowing the page to deselect confirmed sends.
export const sendWhatsappBatch = async ({
  customers, batchSize, message, cleanedNames = {}, attachment, delaySeconds,
  send, shouldStop = () => false, onProgress = () => {}, onSent = () => {},
  onItemResult = () => {}, wait = waitBetweenMessages,
  antiBan = false, skipUnregistered = false,
  cooldownEvery = 15, cooldownSeconds = 15,
}) => {
  const batch = customers.slice(0, batchSize > 0 ? batchSize : customers.length);
  const outcome = { sent: 0, failed: 0, skipped: 0, processed: 0, total: batch.length, errors: [], results: [], stopped: false };

  for (const customer of batch) {
    if (shouldStop()) { outcome.stopped = true; break; }
    const phone = [customer.mobile, customer.mobile2, customer.mobile3]
      .map(value => String(value || '').trim()).find(Boolean);
    const name = cleanedNames[customer.id] || customer.name || '';
    onProgress({ sent: outcome.processed, total: outcome.total, current: name || phone || 'No phone number' });

    if (!phone) {
      outcome.skipped++;
      outcome.errors.push(`${name || 'Customer'}: No phone number`);
      const itemResult = { customer, id: customer.id, name, phone: '', status: 'skipped', error: 'No phone number', timestamp: new Date().toISOString() };
      outcome.results.push(itemResult);
      onItemResult(itemResult);
    } else if (skipUnregistered && customer.whatsapp_ready === false) {
      outcome.skipped++;
      outcome.errors.push(`${name || phone}: Skipped (verified not registered on WhatsApp)`);
      const itemResult = { customer, id: customer.id, name, phone, status: 'skipped', whatsapp_ready: false, error: 'Not registered on WhatsApp', timestamp: new Date().toISOString() };
      outcome.results.push(itemResult);
      onItemResult(itemResult);
    } else {
      const formData = new FormData();
      formData.append('phone', phone);
      formData.append('message', message.replace(/\{name\}/g, name).replace(/\{mobile\}/g, phone));
      if (attachment) formData.append('media', attachment);
      try {
        const response = await send(formData);
        if (response?.data?.success !== true || typeof response?.data?.messageId !== 'string' || !response.data.messageId.trim()) {
          throw new Error(response?.data?.error || 'WhatsApp did not confirm this message was sent');
        }
        outcome.sent++;
        onSent(customer.id);
        const itemResult = { customer, id: customer.id, name, phone, status: 'sent', whatsapp_ready: true, messageId: response.data.messageId, timestamp: new Date().toISOString() };
        outcome.results.push(itemResult);
        onItemResult(itemResult);
      } catch (error) {
        const errMsg = whatsappErrorMessage(error);
        outcome.failed++;
        outcome.errors.push(`${name || phone}: ${errMsg}`);
        const isNotOnWhatsApp = /not registered|invalid phone/i.test(errMsg);
        const itemResult = {
          customer,
          id: customer.id,
          name,
          phone,
          status: 'failed',
          whatsapp_ready: isNotOnWhatsApp ? false : undefined,
          error: errMsg,
          timestamp: new Date().toISOString(),
        };
        outcome.results.push(itemResult);
        onItemResult(itemResult);

        const status = error?.response?.status;
        const isLookupOrValidationError = /lookup timed out|lookup fail|not registered|invalid phone|country code/i.test(errMsg);
        // A timeout may have happened after delivery; pause without retrying it.
        // Recipient lookup timeouts and unregistered numbers are per-recipient validation issues, not batch-stopping server failures.
        if (!isLookupOrValidationError && (!error?.response || status === 401 || status === 403 || status >= 500)) {
          outcome.stopped = true;
        }
      }
    }

    outcome.processed++;
    onProgress({ sent: outcome.processed, total: outcome.total, current: name || phone || '' });
    if (outcome.stopped) break;

    // Anti-ban cooling pause after every cooldownEvery messages
    if (antiBan && outcome.sent > 0 && outcome.sent % cooldownEvery === 0 && outcome.processed < outcome.total && !shouldStop()) {
      onProgress({ sent: outcome.processed, total: outcome.total, current: `🛡️ Anti-ban pause (${cooldownSeconds}s cooling)...` });
      await wait(cooldownSeconds * 1000, shouldStop);
    }

    if (outcome.processed < outcome.total && delaySeconds > 0 && !shouldStop()) {
      const waitMs = antiBan
        ? Math.round(delaySeconds * (0.8 + Math.random() * 0.4) * 1000)
        : delaySeconds * 1000;
      await wait(waitMs, shouldStop);
    }
  }

  return outcome;
};
